<script setup>
import { ref, computed, onMounted } from 'vue';

const props = defineProps({
    options: {
        type: Object,
        required: true
    }
});

const emit = defineEmits(['hide-modal']);

// --- State ---

const items = ref(props.options.items || []);
const itemIndex = ref(0);

// --- Computed ---

/**
 * Returns the current active item object based on the index.
 */
const currentItem = computed(() => items.value[itemIndex.value] || null);

// --- Methods ---

/**
 * Initializes the gallery by finding the index of the item that was clicked.
 */
const init = () => {
    const startId = props.options.id;
    const index = items.value.findIndex(item => item.id === startId);

    if (index !== -1) {
        itemIndex.value = index;
    }
};

/**
 * Navigates to the previous image.
 */
const showPrevious = () => {
    if (itemIndex.value > 0) {
        itemIndex.value--;
        handleOnShow();
    }
};

/**
 * Navigates to the next image.
 */
const showNext = () => {
    if (itemIndex.value < items.value.length - 1) {
        itemIndex.value++;
        handleOnShow();
    }
};

/**
 * Triggers the optional 'onShow' callback provided in the item data.
 */
const handleOnShow = () => {
    if (currentItem.value?.onShow) {
        currentItem.value.onShow();
    }
};

// --- Lifecycle ---

onMounted(() => {
    init();
});
</script>

<template>
    <div class="modal-dialog modal-fullscreen text-light">
        <div class="modal-content bg-transparent border-0">
            <div class="modal-header border-0">
                <button
                    aria-label="Close"
                    class="btn-close btn-close-white fs-4 opacity-100"
                    type="button"
                    @click="$emit('hide-modal')"
                />
            </div>

            <div class="modal-body d-flex align-items-center text-center" @click.self="$emit('hide-modal')">
                <button
                    class="btn btn-lg btn-link fs-1 text-light p-4"
                    :class="{'invisible': itemIndex <= 0}"
                    type="button"
                    @click="showPrevious"
                >
                    <i class="bi-chevron-left" />
                </button>

                <div class="flex-fill px-3">
                    <img
                        v-if="currentItem"
                        :alt="currentItem.title"
                        :src="currentItem.src"
                        class="shadow img-fluid mw-100 mh-100"
                    >
                </div>

                <button
                    class="btn btn-lg btn-link fs-1 text-light p-4"
                    :class="{'invisible': itemIndex >= items.length - 1}"
                    type="button"
                    @click="showNext"
                >
                    <i class="bi-chevron-right" />
                </button>
            </div>

            <div v-if="currentItem" class="d-flex align-items-center px-4 py-3 fs-6 text-light bg-dark bg-opacity-25">
                <p class="flex-fill mb-0">
                    {{ currentItem.title }}
                </p>

                <p class="count mb-0">
                    {{ itemIndex + 1 }} of {{ items.length }}
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal-body {
    /* Ensure clicking the backdrop area closes the modal */
    background-color: rgba(0, 0, 0, 0.85);
    height: 100%;
}

img {
    user-select: none;
    -webkit-user-drag: none;
    object-fit: contain;
    max-height: 85vh;
}

.count {
    font-variant-numeric: tabular-nums;
    letter-spacing: 1px;
}
</style>